import React, { useState } from 'react';
import { limitOrderHandler, marketOrderHandler } from '../../lib/api';
import './PlaceOrders.css';
import { createLogger } from '../../utils/logger';

const log = createLogger('PlaceOrders');

/* ─── New combined Order Entry Widget ──────────────────────── */
export const OrderEntryWidget = ({ selectedStock }) => {
    const [side, setSide] = useState<'buy' | 'sell'>('buy');
    const [orderType, setOrderType] = useState<'market' | 'limit'>('limit');
    const [qty, setQty] = useState<number | ''>(10);
    const [price, setPrice] = useState<number | ''>('');
    const [, setSubscribeVar] = useState(0);

    const handleSubmit = () => {
        if (!selectedStock) {
            log.warn('No stock selected');
            return;
        }
        const isBid = side === 'buy';
        const volume = qty === '' ? 0 : qty;

        if (orderType === 'limit') {
            limitOrderHandler(
                { ticker: selectedStock, volume, isBid, price: Number(price) },
                setSubscribeVar,
            );
            log.info('Limit order placed', { selectedStock, side, volume, price });
        } else {
            marketOrderHandler({ ticker: selectedStock, volume, isBid }, setSubscribeVar);
            log.info('Market order placed', { selectedStock, side, volume });
        }
    };

    return (
        <div className="order-entry-widget">
            {/* Buy / Sell tabs */}
            <div className="order-entry-sides">
                <button
                    className={`side-tab buy-tab ${side === 'buy' ? 'active' : ''}`}
                    onClick={() => setSide('buy')}
                >
                    Buy
                </button>
                <button
                    className={`side-tab sell-tab ${side === 'sell' ? 'active' : ''}`}
                    onClick={() => setSide('sell')}
                >
                    Sell
                </button>
            </div>

            {/* Ticker + Order type */}
            <div className="order-entry-meta">
                <span className="order-entry-ticker">{selectedStock || '—'}</span>
                <div className="order-type-tabs">
                    <button
                        className={`type-tab ${orderType === 'market' ? 'active' : ''}`}
                        onClick={() => setOrderType('market')}
                    >
                        Market
                    </button>
                    <button
                        className={`type-tab ${orderType === 'limit' ? 'active' : ''}`}
                        onClick={() => setOrderType('limit')}
                    >
                        Limit
                    </button>
                </div>
            </div>

            {/* Input fields */}
            <div className="order-entry-fields">
                <div className="order-field">
                    <label>Quantity</label>
                    <input
                        type="number"
                        value={qty === '' ? '' : qty}
                        onChange={(e) =>
                            setQty(e.target.value === '' ? '' : Number(e.target.value))
                        }
                        placeholder="0"
                        min="0"
                    />
                </div>
                {orderType === 'limit' && (
                    <div className="order-field">
                        <label>Price</label>
                        <input
                            type="number"
                            value={price}
                            onChange={(e) =>
                                setPrice(e.target.value === '' ? '' : Number(e.target.value))
                            }
                            placeholder="0.00"
                            min="0"
                        />
                    </div>
                )}
            </div>

            {/* Submit */}
            <button className={`order-submit-btn ${side}`} onClick={handleSubmit}>
                {side === 'buy' ? `Buy ${selectedStock || ''}` : `Sell ${selectedStock || ''}`}
            </button>
        </div>
    );
};

/* ─── Legacy widgets (kept for backward compatibility) ──────── */
export const LimitOrdersWidget = ({ selectedStock }) => {
    const [price, setPrice] = useState<number | ''>('');
    const [amount, setAmount] = useState<number | ''>(10);
    const [, setSubscribeVar] = useState(0);

    const handleBuy = () => {
        if (!selectedStock) {
            log.warn('No stock selected for buying');
            return;
        }
        limitOrderHandler(
            {
                ticker: selectedStock,
                volume: amount === '' ? 0 : amount,
                isBid: true,
                price: Number(price),
            },
            setSubscribeVar,
        );
        log.info('Buy limit order placed', {
            selectedStock,
            amount,
            price: price || 'market price',
        });
    };

    const handleSell = () => {
        if (!selectedStock) {
            log.warn('No stock selected for selling');
            return;
        }
        limitOrderHandler(
            {
                ticker: selectedStock,
                volume: amount === '' ? 0 : amount,
                isBid: false,
                price: Number(price),
            },
            setSubscribeVar,
        );
        log.info('Sell limit order placed', {
            selectedStock,
            amount,
            price: price || 'market price',
        });
    };

    return (
        <div className="widget-container">
            <div className="buy-sell-flex">
                <div className="buy-sell-column-1">
                    <button className="buy-button" onClick={handleBuy}>
                        Buy
                    </button>
                    <button className="sell-button" onClick={handleSell}>
                        Sell
                    </button>
                </div>
                <div className="buy-sell-column-2">
                    <label htmlFor="volume">Volume:</label>
                    <label htmlFor="price">Price:</label>
                </div>
                <div className="buy-sell-column-3">
                    <div className="input-group">
                        <input
                            id="volume"
                            type="number"
                            className={`quantity-input ${amount === 0 || amount === '' ? 'placeholder-visible' : ''}`}
                            value={amount === '' ? '' : amount}
                            onChange={(e) => {
                                const value = e.target.value;
                                setAmount(value === '' ? '' : Number(value));
                            }}
                            placeholder="Enter Quantity"
                        />
                    </div>
                    <div className="input-group">
                        <input
                            id="price"
                            type="number"
                            className={`price-input ${!price && price !== 0 ? 'placeholder-visible' : ''}`}
                            value={price}
                            onChange={(e) =>
                                setPrice(e.target.value === '' ? '' : Number(e.target.value))
                            }
                            placeholder="Limit price"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export const SelectedStockWidget = ({ selectedStock }) => {
    return (
        <div className="SelectedStockWidget">
            <h3>Trade {selectedStock || ''}</h3>
        </div>
    );
};

export const OrderType = ({ orderType, setOrderType }) => {
    return (
        <div className="OrderType">
            <span className="choose-title">Type</span>
            <button
                className={`market-button ${orderType === 'market' ? 'selected' : ''}`}
                onClick={() => setOrderType('market')}
            >
                Market
            </button>
            <button
                className={`limit-button ${orderType === 'limit' ? 'selected' : ''}`}
                onClick={() => setOrderType('limit')}
            >
                Limit
            </button>
        </div>
    );
};
